import React from 'react';
import type {
  Venue,
  Product,
  App,
  PosProduct,
  MenuOption,
  PosOption,
  PosVariant,
  Category,
  MenuVariant,
  VariantVariablesByApp,
  ProductVariant,
  ProductVariablesByApp
} from 'generated/graphql';
import { getEnvironmentVariables } from 'services/environment-variables/environment-variables';
import { AmplitudeEventCategories, AmplitudeEventProperties, logAmplitudeEvent } from 'utils/Amplitude';
import { type NavMenuItem, type Option } from 'types';
import { type LogoutOptions } from '@auth0/auth0-react';

import overviewIcon from 'icons/ic_Overview.svg';
import appsIcon from 'icons/ic_Apps.svg';
import menusIcon from 'icons/ic_Menus.svg';
import accountIcon from 'icons/ic_Account.svg';
import paymentsIcon from 'icons/payment.svg';

import { useModalContext } from 'common/Context/ModalContext';
import { ApolloError } from '@apollo/client';

import AppErrorList from 'components/AppErrorList/AppErrorList';
import cuid from 'cuid';
import { DateTime } from 'luxon';
import { DEFAULT_TIMEZONE } from 'consts';
import { venueHasBrands } from './brand';
import { AvailabilityStatus } from './constants';

export const fallBackImage = '/temp/profile.png';

type Item = Product | MenuVariant;

export const formatPrice = (price: number | string | null | undefined): string => {
  if (price === null || typeof price === 'undefined') {
    return `Invalid price`;
  }

  const response = typeof price === 'string' ? parseInt(price, 10) : price;

  if (Number.isNaN(response)) {
    return `Invalid price`;
  }

  return `$${(response / 100).toFixed(2)}`;
};

export const getFilteredMenuItems = (menuItems: Item[], searchValue: string): Item[] => {
  return menuItems
    .filter((item: Item) => {
      return (
        item.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        item.posId.toLowerCase().includes(searchValue.toLowerCase())
      );
    })
    .sort((a: Item, b: Item) => {
      // Sort alphabetically first
      const aName = a.name.toUpperCase();
      const bName = b.name.toUpperCase();

      if (aName < bName) {
        return -1;
      }

      if (aName > bName) {
        return 1;
      }

      return 0;
    })
    .sort((a: Item, b: Item) => {
      if (searchValue) {
        const aStartsWithTerm = a.name.toLowerCase().startsWith(searchValue.toLowerCase());
        const bStartsWithTerm = b.name.toLowerCase().startsWith(searchValue.toLowerCase());

        if (a.name && aStartsWithTerm && !bStartsWithTerm) {
          return -1;
        }

        if (b.name && !aStartsWithTerm && bStartsWithTerm) {
          return 1;
        }
      }

      return 0;
    });
};

export const getConnectUrl = (venueId: string | undefined, route?: string): string => {
  const { CONNECT_URL } = getEnvironmentVariables();
  if (!venueId) {
    return CONNECT_URL;
  }
  return `${CONNECT_URL}/venue/${venueId}${route ? `/${route}` : ''}`;
};

export const getRandomLengthString = (min = 3, max = 10): string => {
  const randomLength = Math.floor(Math.random() * (max - min + 1) + min);

  return 'X'.repeat(randomLength);
};

export function getNavMenuItems(venueId: string | undefined): NavMenuItem[] {
  const { CONNECT_URL } = getEnvironmentVariables();
  return [
    {
      label: 'Overview',
      url: getConnectUrl(venueId),
      icon: overviewIcon,
      mobileOnly: false
    },
    {
      label: 'Apps',
      url: getConnectUrl(venueId, 'apps'),
      icon: appsIcon,
      mobileOnly: false
    },
    {
      label: 'Menus',
      url: '/',
      isActive: true,
      icon: menusIcon,
      mobileOnly: false
    },
    {
      label: 'Account',
      url: `${CONNECT_URL}/profile`,
      icon: accountIcon,
      mobileOnly: true
    },
    {
      label: 'Insights',
      url: getConnectUrl(venueId, 'insights'),
      icon: paymentsIcon,
      miniLabel: 'New',
      mobileOnly: false
    }
  ];
}

export const getSettingsOptions = (logoutFn: (options?: LogoutOptions) => void, venueId?: string): Option[] => {
  const { CONNECT_URL, SUPPORT_GUIDE_URL, PRODUCTBOARD_PORTAL_URL } = getEnvironmentVariables();
  return [
    {
      content: <span>Settings</span>,
      value: 'settings',
      url: `${CONNECT_URL}/profile`,
      openNewPage: false,
      type: 'link'
    },
    {
      content: <span>Help &amp; Support</span>,
      value: 'help',
      url: SUPPORT_GUIDE_URL,
      openNewPage: true,
      type: 'link'
    },
    {
      content: <span>Product Releases</span>,
      value: 'product-releases',
      type: 'button',
      callback: (): void => {
        logAmplitudeEvent({
          name: 'click product releases',
          category: AmplitudeEventCategories.NAVIGATION,
          properties: {
            [AmplitudeEventProperties.VENUE_ID]: venueId || ''
          }
        });
        window.open(PRODUCTBOARD_PORTAL_URL, '_blank');
      }
    },
    {
      content: <span>Log out</span>,
      value: 'log-out',
      type: 'button',
      callback: (): void => logoutFn()
    }
  ];
};

export const formatListOfNames = (words: string[]): string => {
  let response = '';

  words.forEach((word, index: number) => {
    if (index) {
      if (index === words.length - 1) {
        response += ' and ';
      } else {
        response += ', ';
      }
    }

    response += word;
  });

  return response;
};

export const listAppsInString = (apps: App[], hasBrands: boolean, venueName?: string): string => {
  return formatListOfNames(
    apps.map(({ name, brand }) => (hasBrands ? `${name} (${brand?.name ?? venueName ?? ''})` : name))
  );
};

export const sanitiseUrlParam = (param: string | undefined): string => {
  if (!param) {
    return '';
  }

  return param.replace(/[^a-zA-Z0-9-_]/g, '');
};

type ErrorModalArgs = (error: ApolloError | undefined, currentVenue: Venue | undefined | null) => void;

export const useErrorModal = (): ErrorModalArgs => {
  const { openModal, closeModal } = useModalContext();
  const { SUPPORT_FORM_URL, CONNECT_URL } = getEnvironmentVariables();
  return (error: ApolloError | undefined, currentVenue: Venue | undefined | null): void => {
    let requestId = null;
    let mmCode = null;
    const { graphQLErrors, extraInfo } = error || {};

    if (graphQLErrors?.length) {
      const [err] = graphQLErrors;
      requestId = err.extensions?.requestId;
      mmCode = err.extensions?.mmCode;
    }

    const formUrl = new URL(SUPPORT_FORM_URL);
    formUrl.searchParams.set('support_category', 'Menu Issues');
    let description = 'Insert your issue here...\n\n';
    if (currentVenue?.hashedId) {
      description += `venueId: ${currentVenue.hashedId}\n`;
    }
    if (typeof requestId === 'string') {
      description += `requestId: ${requestId}\n`;
    }
    formUrl.searchParams.set('content', description);

    if (currentVenue?.name) {
      formUrl.searchParams.set('venue_name', currentVenue.name);
    }

    const hasBrands = venueHasBrands(currentVenue?.menus ?? []);

    const [title, preBodyContent, body, buttons, canDismiss = true] = (() => {
      switch (mmCode) {
        case 3002:
        case 3003:
          formUrl.searchParams.set('subject', 'Menu management Support');

          return [
            'Oh sweet potatoes, something went wrong.',
            null,
            <>
              Please try again or{' '}
              <a href={formUrl.href} target="_blank" rel="noreferrer">
                contact our support team
              </a>
              .
            </>,
            [
              {
                label: 'OK',
                url: CONNECT_URL,
                level: 'high'
              }
            ]
          ];
        case 6002:
          return [
            'This item has been marked as unavailable in your POS.',
            null,
            'Please update this item availability from your POS.',
            [
              {
                label: 'OK',
                callback: closeModal,
                level: 'high'
              }
            ]
          ];
        case 5001:
          // eslint-disable-next-line no-case-declarations
          const venueId = window.location.pathname.includes('/venue/')
            ? window.location.pathname.split('/venue/')[1]
            : '';

          return [
            `We’re busy making some refurbishments.`,
            null,
            `We’re excited to welcome you back once we’re done.`,
            [
              {
                label: 'OK',
                url: `${CONNECT_URL}${venueId ? `/venue/${venueId}` : ''}`,
                level: 'high'
              }
            ],
            false
          ];
        case 6003:
          if (extraInfo?.subject) {
            formUrl.searchParams.set('subject', extraInfo.subject);
          }
          if (extraInfo?.failedApps.length) {
            description += `Update Failing for ${listAppsInString(
              extraInfo.failedApps,
              hasBrands,
              currentVenue?.name
            )}`;

            formUrl.searchParams.set(`content`, description);
          }
          return [
            `Sorry, your item couldn't be updated in ${listAppsInString(
              extraInfo.failedApps,
              hasBrands,
              currentVenue?.name
            )}.`,
            <AppErrorList key="1" apps={extraInfo.failedApps} />,
            <>
              Please try again later. If you keep seeing this message, please{' '}
              <a href={formUrl.href} target="_blank" rel="noreferrer">
                let us know
              </a>
              .
            </>,
            [
              {
                label: 'OK',
                callback: window.location.reload.bind(window.location),
                level: 'high'
              }
            ]
          ];
        default:
          formUrl.searchParams.set(`subject`, `Menu management Support`);

          return [
            'Oh sweet potatoes, something went wrong.',
            null,
            <>
              Please try again or{' '}
              <a href={formUrl.href} target="_blank" rel="noreferrer">
                contact our support team
              </a>
              .
            </>,
            [
              {
                label: 'OK',
                callback: closeModal,
                level: 'high'
              }
            ]
          ];
      }
    })();

    openModal({
      type: 'ERROR',
      title,
      preBodyContent,
      body,
      buttons,
      canDismiss
    });
  };
};

export const getAvailabilitySlug = (availability: string): string => {
  return availability !== AvailabilityStatus.UNAVAILABLE ? AvailabilityStatus.AVAILABLE : availability;
};

export const ucFirst = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const useAvailabilityTypeByVariablesByApp = (
  variablesByApp: ProductVariablesByApp[] | VariantVariablesByApp[]
): string => {
  const { AVAILABILITY_BY_VARIANT_WHITELISTED_APP_IDS } = getEnvironmentVariables();
  let isAllAvailable = true;
  let isAllUnavailable = true;
  let hasCheckedAvailability = false;
  let isItemSnoozed = false;

  variablesByApp.forEach(({ __typename, availability, app, snoozedUntil }) => {
    // Exclude non whitelisted apps from the availability check for variants only
    const allowAvailabilityCheck =
      __typename !== 'VariantVariablesByApp' ||
      (__typename === 'VariantVariablesByApp' &&
        AVAILABILITY_BY_VARIANT_WHITELISTED_APP_IDS.includes(
          Number(app.id.startsWith('virtual') ? app.id.split('.')[1] : app.id)
        ));
    if (snoozedUntil && new Date(snoozedUntil) > new Date()) {
      isItemSnoozed = true;
    }
    if (allowAvailabilityCheck) {
      if (availability !== AvailabilityStatus.UNAVAILABLE) {
        isAllUnavailable = false;
      } else {
        isAllAvailable = false;
      }
      hasCheckedAvailability = true;
    }
  });

  // If no availability check was performed (all checks were skipped due to no apps being whitelisted), fallback to available
  if (!hasCheckedAvailability) {
    return AvailabilityStatus.AVAILABLE;
  }

  if (isAllAvailable) {
    return AvailabilityStatus.AVAILABLE;
  }

  if (isAllUnavailable) {
    return isItemSnoozed ? AvailabilityStatus.UNAVAILABLE_TODAY : AvailabilityStatus.UNAVAILABLE;
  }

  return AvailabilityStatus.MIXED;
};

export const formatTextToPriceInt = (value: string): number => {
  const valueWithoutLetters = value.replace(/[^0-9.]/g, '');

  const removePrecedingZeroes =
    valueWithoutLetters !== '0' ? valueWithoutLetters.replace(/^0+/, '') : valueWithoutLetters;

  return parseInt((parseFloat(removePrecedingZeroes) * 100).toFixed(0), 10);
};

export const unSlugifyAndCapitalise = (str: string): string => {
  return ucFirst(str.replace(/-/g, ' '));
};

export const getThemeClassNameByIndex = (index: number): string => {
  switch (index % 3) {
    case 0:
      return 'GrapefruitRed';
    case 1:
      return 'OysterBlueberry';
    default:
      return 'MintKale';
  }
};

// Used to check if string is an md5 hash. All MMIds are currently MD5 Hashes
export const isMD5Hash = (string: string): boolean => {
  const regexExp = /^[a-f0-9]{32}$/gi;
  return regexExp.test(string);
};

export const emboldenSearchTerm = (str: string, searchTerm: string): JSX.Element => {
  const trimmedTextValue = searchTerm.trim().toLowerCase();

  const indexOfTerm = str.toLowerCase().indexOf(trimmedTextValue);

  const term = str.substring(indexOfTerm, indexOfTerm + trimmedTextValue.length);
  const beforeTerm = str.substring(0, indexOfTerm);
  const afterTerm = str.substring(indexOfTerm + trimmedTextValue.length, str.length);

  return (
    <span>
      {beforeTerm}
      <strong>{term}</strong>
      {afterTerm}
    </span>
  );
};

export const posVariantToMMVariant = (variant: PosVariant, apps: App[]): ProductVariant => {
  return {
    __typename: 'ProductVariant',
    mmId: cuid(),
    name: variant.name,
    posId: variant.posId,
    variablesByApp: apps.map((app) => {
      return {
        app,
        __typename: 'VariantVariablesByApp',
        price: variant.price,
        availability: 'available'
      };
    })
  };
};

export const posOptionToMMOption = (option: PosOption, apps: App[]): MenuOption => {
  return {
    __typename: 'MenuOption',
    name: option.name,
    posId: option.posId,
    min: option.min,
    max: option.max,
    mmId: cuid(),
    source: option.source || 'pos-product',
    customPosId: option.customPosId,
    variants: option.variants.map((variant) => posVariantToMMVariant(variant, apps))
  };
};

export const posProductToMMProduct = (posProduct: PosProduct, temporaryID: string, apps: App[]): Product => {
  return {
    __typename: 'Product',
    mmId: temporaryID,
    posId: posProduct.posId,
    name: posProduct.name,
    type: posProduct.type,
    customPosId: posProduct.customPosId,
    description: posProduct.description,
    tags: posProduct.tags,
    dietary: posProduct.dietary,
    menuDir: [],
    includedItems: [],
    bundledItems: [],
    surcounts: [],
    imageUri: posProduct.imageUri,
    options: posProduct.options?.map((option) => posOptionToMMOption(option, apps)),
    variablesByApp: apps.map((app) => {
      return {
        app,
        availability: posProduct.availability,
        price: posProduct.unitPrice,
        imageUri: posProduct.imageUri
      };
    })
  };
};

export const addAppsToVariantVariablesByApps = (variant: ProductVariant, apps: App[]): ProductVariant => ({
  ...variant,
  variablesByApp: variant.variablesByApp.length
    ? [
        ...variant.variablesByApp,
        ...apps
          .filter(({ id }) => !variant.variablesByApp.some((variablesByApp) => variablesByApp.app.id === id))
          .map(
            (app): VariantVariablesByApp => ({
              app,
              __typename: 'VariantVariablesByApp',
              price: variant.variablesByApp[0].price,
              availability: variant.variablesByApp[0].availability
            })
          )
      ]
    : []
});

export const addAppsToOptionVariablesByApps = (option: MenuOption, apps: App[]): MenuOption => ({
  ...option,
  __typename: 'MenuOption',
  variants: option.variants.map((variant) => addAppsToVariantVariablesByApps(variant, apps))
});

export const addAppsToProductVariantsByApp = (product: Product, apps: App[], posPrice?: number | null): Product => ({
  ...product,
  __typename: 'Product',
  options: product.options.map((option) => addAppsToOptionVariablesByApps(option, apps)),
  variablesByApp: product.variablesByApp.length
    ? [
        ...product.variablesByApp,
        ...apps
          .filter(({ id }) => !product.variablesByApp.some((varByApp) => varByApp.app.id === id))
          .map(
            (app): ProductVariablesByApp => ({
              app,
              availability: product.variablesByApp[0].availability,
              price: posPrice || product.variablesByApp[0].price,
              __typename: 'ProductVariablesByApp'
            })
          )
      ]
    : []
});

/**
 * @deprecated Use cache.identify() instead
 */
export const convertMmIdToProductCacheKey = (mmId: string): string => {
  return `Product:{"mmId":"${mmId}"}`;
};

export const categorySort = (a: Category, b: Category): number => {
  if (a.mmId === 'UNCATEGORISED') return 1;
  if (b.mmId === 'UNCATEGORISED') return -1;
  return a.displayOrder - b.displayOrder;
};
export const CATEGORY_ITEM_IDENTIFIER = ':doshiicat:';
export const DOSHII_VIRTUAL_ITEM_IDENTIFIER = 'doshii:virtual:';
export const DOSHII_ITEM_VARIANT_IDENTIFIER = 'doshii:item:';

export const convertBooleanToAvailability = <T extends boolean>(
  value: T
): T extends true ? 'available' : 'unavailable' => {
  return (value ? 'available' : 'unavailable') as T extends true ? 'available' : 'unavailable';
};

export const setDateToTomorrowAt4AM = (timezone?: string | null): string => {
  // Create a DateTime object in the provided timezone
  const now = DateTime.now().setZone(timezone || DEFAULT_TIMEZONE);

  // Add 1 day to get tomorrow and set the time to 4:00:00 AM
  const tomorrowAt4am = now.plus({ days: 1 }).set({ hour: 4, minute: 0, second: 0, millisecond: 0 });

  // Return as ISO string
  return tomorrowAt4am.isValid ? tomorrowAt4am.toISO() : '';
};
